import * as _ from "lodash";
import { AnyAction, applyMiddleware, combineReducers, createStore, Reducer, Store } from "redux";
import { enableBatching } from "redux-batched-actions";
import { composeWithDevTools } from "redux-devtools-extension";
import { ignoreActions } from "redux-ignore";
import thunk from "redux-thunk";
import undoable, { ActionTypes, StateWithHistory } from "redux-undo";
import { BATCH_ACTION } from "../Constants";
import { IPage } from "../widgets/interfaces/model";
import {
	canvasEditor,
	CanvasEditorConstants,
	clipboard, guides,
	PageActionConstants,
	pages,
	SelectionConstants,
	selectionState as selection,
} from "./reducers/index";

/**
 * Middleware to block all actions if canvas is not in edit mode
 * @param  {any} store
 * @returns { action | null }
 */
export const editEnabled = (store: any) => (next: any) => (action: any) => {
	let actionToCheck = action;
	if (action.type === "BATCHING_REDUCER.BATCH") {
		actionToCheck = action.payload[0];
	}
	if (store.getState().canvasEditor.isEditEnabled
        || actionToCheck.type === CanvasEditorConstants.DUMMY_ACTION
        || actionToCheck.type === CanvasEditorConstants.ENABLE_EDITING
        || actionToCheck.type === CanvasEditorConstants.SET_FROALA_LICENSE
        || actionToCheck.type === CanvasEditorConstants.SET_CANVAS_CONTAINER
        || actionToCheck.type === CanvasEditorConstants.SET_IS_CANVAS_TEMPLATE
        || actionToCheck.type === PageActionConstants.ADD_PAGE
        || actionToCheck.type === PageActionConstants.SET_PAGES
        || actionToCheck.type === PageActionConstants.TRANSLATE_OBJECTS
        || actionToCheck.type === PageActionConstants.TOGGLE_PLACEHOLDER_LABELS_OPTIONALLY
        || actionToCheck.type === SelectionConstants.SELECT_PAGE
        || actionToCheck.type === SelectionConstants.SET_SHAPE_DEFAULT_PROPERTIES
        || action.type === SelectionConstants.SET_ALL_TEXT_DEFAULTS_STYLES
        || action.type === SelectionConstants.SET_TEXT_SHAPE_DEFAULT_PROPERTIES) {
		const results = next(action);
		return results;
	}
	return null;
};

/**
 * Reset format painter middleware which is being used to reset format painter option if format painter is active
 * and any other action get trigger other than SELECT OBJECT
 */
export const resetFormatPainter = (store: any) => (next: any) => (action: any) => {
	const state = store.getState();
	if (state.canvasEditor.isEditEnabled
        && state.canvasEditor.isFormatPainterActive
        && action.type === BATCH_ACTION
        && action.payload[0].type !== SelectionConstants.SELECT_OBJECT
        && !action.payload[0].type.isMultiSelect
	) {
		next({
			isFormatPainter: false,
			transient: true,
			type: CanvasEditorConstants.SET_FORMAT_PAINTER,
		});
	}

	const results = next(action);
	return results;
};

/**
 * Check if action is belong to undoable actions
 * @param type {string}
 */
const isUndoableActions = (type: string) => {
	return ActionTypes.REDO === type
        || ActionTypes.UNDO === type
        || ActionTypes.CLEAR_HISTORY === type;
};

let canvasStore: Store<{}>;

export const trackTransient = (reducer: Reducer<StateWithHistory<IPage[]>>) =>
	(state: StateWithHistory<IPage[]>, action: AnyAction) => {
		let newState = state;
		if (newState && newState.present.length && !action.transient) {
			newState = _.cloneDeep(state);
			const page = newState.present[0];
			page.contentChanges = 1 + (page.contentChanges || 0);
		}
		return reducer(newState, action);
	};

const initStore = (initialState: any, isPrimaryInstance = false) => {
	const store = createStore(enableBatching(combineReducers({
		canvasEditor,
		pages: ignoreActions(trackTransient(undoable(pages, {
			filter: (action: any, currentState, previousHistory) => {
				return !action.transient;
			},
			syncFilter: true,
		})), (action) => {
			// ignore action if it's not related to pages'
			const allowedAction = (PageActionConstants as any)[action.type] || isUndoableActions(action.type);
			return !allowedAction;
		}),
		selection: ignoreActions(undoable(selection, {
			filter: (action: any, currentState, previousHistory) => {
				return !action.transient && (action.type === SelectionConstants.STORE_OBJECT_SELECTIONS);
			}, syncFilter: true,
		}), (action) => {
			const allowedAction = (SelectionConstants as any)[action.type] || isUndoableActions(action.type);
			return !allowedAction;
		}),
		guides,
		clipboard,
	})), initialState, composeWithDevTools(applyMiddleware(thunk, editEnabled, resetFormatPainter)));
	if (isPrimaryInstance) {
		canvasStore = store;
	}
	return store;
};

const getStore = () => canvasStore;

// Export redux registry and redux store dispatch method
export { initStore, getStore };
